


import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { FiVideoOff, FiMonitor, FiPenTool, FiX } from "react-icons/fi";

const ThumbnailVideo = ({
  uid,
  stream,
  userName,
  onClick,
  isZoomed,
  videoEnabled,
  isScreenShare = false,
  expanded = false,
  isMobile = false,
  isWhiteboard = false,
  showWhiteboard = false,
  onCloseWhiteboard,
}) => {
  const videoRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);

  const heightClass = expanded ? "h-40" : "h-28";
  const widthClass = isMobile ? "w-full" : "";

  const bgBorderClass = useMemo(() => {
    if (isWhiteboard) return "bg-gradient-to-br from-purple-900 to-indigo-800 border-purple-400";
    if (isScreenShare) return "bg-purple-500/20 border-purple-400";
    if (uid === "streamer") return "bg-blue-500/20 border-blue-400";
    return "bg-green-500/20 border-green-400";
  }, [isWhiteboard, isScreenShare, uid]);

  const statusDotClass = useMemo(() => {
    if (isScreenShare) return "bg-purple-500 shadow-[0_0_6px_rgba(168,85,247,0.6)]";
    if (uid === "streamer") return "bg-blue-500 shadow-[0_0_6px_rgba(59,130,246,0.6)]";
    return "bg-green-500 shadow-[0_0_6px_rgba(34,197,94,0.6)]";
  }, [isScreenShare, uid]);

  const handleCloseWhiteboard = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      onCloseWhiteboard?.();
    },
    [onCloseWhiteboard]
  );

  // ✅ FIX: stream.id use karo - same MediaStream object pe reset nahi hoga
  useEffect(() => {
    setIsLoaded(false);
  }, [stream?.id]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !stream) return;

    if (el.srcObject !== stream) {
      el.srcObject = stream;
    }

    let cancelled = false;

    const tryPlay = () => {
      el.play().catch(() => {});
    };

    const handleLoadedData = () => {
      if (cancelled) return;
      setIsLoaded(true);
      tryPlay();
    };

    el.addEventListener("loadeddata", handleLoadedData);
    tryPlay();

    if (el.readyState >= 2) {
      setIsLoaded(true);
    }

    return () => {
      cancelled = true;
      el.removeEventListener("loadeddata", handleLoadedData);
    };
  }, [stream?.id]); // ✅ FIX: stream reference ki jagah stream.id use karo

  if (isWhiteboard) {
    return (
      <div
        className={`relative ${heightClass} ${widthClass} bg-gradient-to-br from-purple-900 to-indigo-800 rounded-lg overflow-hidden cursor-pointer transition-all group ${
          isZoomed
            ? "border-2 border-purple-500 shadow-lg shadow-purple-500/20"
            : "hover:ring-2 hover:ring-purple-400 border border-purple-800"
        }`}
        onClick={onClick}
        style={{ transform: "translateZ(0)", backfaceVisibility: "hidden", perspective: "1000px" }}
      >
        <div className="w-full h-full flex flex-col items-center justify-center bg-purple-800/30">
          <FiPenTool className={`${expanded ? "h-8 w-8" : "h-6 w-6"} text-white mb-1`} />
          <div className={`${expanded ? "text-sm" : "text-xs"} text-white font-medium`}>
            Whiteboard
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
          <div className="flex items-center justify-between">
            <div className="text-xs truncate font-medium text-white">
              <span className="truncate">Collaborative Whiteboard</span>
            </div>
          </div>
        </div>

        <div className="absolute top-1.5 right-1.5 rounded-full w-2.5 h-2.5 bg-purple-500 shadow-[0_0_6px_rgba(168,85,247,0.6)] animate-pulse" />

        {isZoomed && (
          <div className="absolute top-1.5 left-1.5 bg-purple-500 text-white text-[10px] px-1.5 py-0.5 rounded">
            Active
          </div>
        )}

        <button
          onClick={handleCloseWhiteboard}
          className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 rounded-full p-1 transition-colors opacity-0 group-hover:opacity-100"
          title="Close Whiteboard"
        >
          <FiX className="w-3 h-3 text-white" />
        </button>

        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-t from-black/40 to-transparent transition-opacity duration-200 pointer-events-none" />
      </div>
    );
  }

  return (
    <div
      className={`relative ${heightClass} ${widthClass} bg-gray-900 rounded-lg overflow-hidden cursor-pointer transition-all group ${
        isZoomed ? `${bgBorderClass} border-2` : "hover:ring-2 hover:ring-blue-400 border border-gray-600"
      }`}
      onClick={onClick}
      style={{ transform: "translateZ(0)", backfaceVisibility: "hidden", perspective: "1000px" }}
    >
      <div className="relative w-full h-full">
        <video
          ref={videoRef}
          muted={uid === "streamer"}
          autoPlay
          playsInline
          className={`w-full h-full object-cover transition-opacity duration-300 ${
            isLoaded ? "opacity-100" : "opacity-0"
          }`}
          style={{ transform: "translateZ(0)", backfaceVisibility: "hidden" }}
          preload="metadata"
        />

        {!isLoaded && (
          <div className="absolute inset-0 bg-gray-800 animate-pulse flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-gray-600 border-t-blue-500 rounded-full animate-spin" />
          </div>
        )}
      </div>

      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
        <div className="flex items-center justify-between">
          <div className="text-xs truncate font-medium text-white">
            <span className="truncate">{userName}</span>
            {isScreenShare && <FiMonitor className="inline ml-1 h-3 w-3 text-purple-300" />}
            {uid === "streamer" && !videoEnabled && (
              <span className="ml-1 text-gray-300">(Off)</span>
            )}
          </div>
        </div>
      </div>

      <div className={`absolute top-1.5 right-1.5 rounded-full w-2.5 h-2.5 ${statusDotClass} ${isLoaded ? "animate-pulse" : ""}`} />

      {isZoomed && (
        <div className="absolute top-1.5 left-1.5 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded">
          Zoomed
        </div>
      )}

      {uid === "streamer" && !videoEnabled && (
        <div className="absolute inset-0 bg-gray-900/90 flex flex-col items-center justify-center">
          <FiVideoOff className="h-8 w-8 text-gray-400 mb-2" />
          <span className="text-xs text-gray-300">Camera Off</span>
        </div>
      )}

      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-t from-black/40 to-transparent transition-opacity duration-200 pointer-events-none" />
    </div>
  );
};

export default React.memo(ThumbnailVideo, (prev, next) => {
  return (
    prev.uid === next.uid &&
    prev.stream?.id === next.stream?.id && // ✅ stream.id compare karo
    prev.userName === next.userName &&
    prev.isZoomed === next.isZoomed &&
    prev.videoEnabled === next.videoEnabled &&
    prev.isScreenShare === next.isScreenShare &&
    prev.expanded === next.expanded &&
    prev.isMobile === next.isMobile &&
    prev.isWhiteboard === next.isWhiteboard &&
    prev.showWhiteboard === next.showWhiteboard &&
    prev.onClick === next.onClick &&
    prev.onCloseWhiteboard === next.onCloseWhiteboard
  );
});

